import React, { useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useAsyncValue, useNavigate } from 'react-router-dom';
import { formActionValidationNaming } from '../../../../../components/forms/ActionForms/constants';

// Components
import MaterialsForm from '../../../../../components/forms/ActionForms/MaterialsForm';
import { Typography } from '../../../../../components/display/Typography';

// Types
import { MaterialFormValues } from '../../../../../components/forms/ActionForms/MaterialsForm/types';
import { OrderActionReferences } from '../types';
import { Role } from '../../../../../types/auth';
import { OrderState } from '../../../../../types/orders';

// Redux
import { roidOTSelected } from '../../../../../state/actions/ui/roidOTSelectedGeneralView';

// Hooks
import { useAuth } from '../../../../../hooks/useAuth';
import { useActionValidationSubmit } from '../useActionValidationSubmit';

export default function MaterialsValidationFormPage(): JSX.Element {
  const { t } = useTranslation('validation');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loaderData = useAsyncValue() as MaterialFormValues &
    OrderActionReferences & { isBasicAction?: boolean; state?: string };

  // Get current user role - RoomTeam can only view, not edit
  const { user } = useAuth();
  const userRole = (user?.user?.rolActive ?? null) as Role | null;
  const isReadOnly = userRole === Role.RoomTeam;

  // Actuació bàsica: Materiales es su PASO TERMINAL desde 2026-08-11 (la
  // pestaña de fauna se retiró). Aquí lleva el pie completo de
  // Guardar/Validar/Pendent de certificar y hace el submit; en la actuación
  // normal sigue siendo un paso intermedio que salta a Maquinaria.
  //
  // Spec: docs/superpowers/specs/2026-08-11-basica-sin-fauna-design.md
  const isBasicAction = !!loaderData?.isBasicAction;
  const isPartial = loaderData?.state === 'partial';

  const { onFormSubmit: submitTerminal, error, setIntent } =
    useActionValidationSubmit({
      isBasicAction,
      isReadOnly,
      userRole,
      terminalTab: 'materials',
    });

  const onFormSubmit = () => {
    if (isReadOnly) return; // RoomTeam cannot submit
    if (isBasicAction) {
      submitTerminal();
      return;
    }
    navigate('../machinery');
  };

  useLayoutEffect(() => {
    dispatch(
      roidOTSelected('actions-validation', loaderData.roid!, loaderData.orderId)
    );
  }, []);

  return (
    <>
      <MaterialsForm
        onFormSubmit={isReadOnly ? () => {} : onFormSubmit}
        initialData={loaderData}
        sessionStorageKey={
          formActionValidationNaming.NEW_VALIDATION_MATERIALS_FORM
        }
        updateKey={formActionValidationNaming.DATA_CHANGES}
        textButton={isReadOnly ? t('viewOnly') : userRole === Role.Tablet ? t('buttons:continue') : isBasicAction && isPartial ? t('common:buttons.save') : t('validate')}
        isTerminalStep={isBasicAction}
        // Mismas reglas que el pie de Animales en la actuación normal — es el
        // mismo componente, así que no hay dos criterios que puedan divergir.
        showSaveButton={!isReadOnly && userRole !== Role.Tablet && !isPartial}
        saveButtonText={t('common:buttons.save')}
        onBeforeSave={() => setIntent('save')}
        onBeforeValidate={() => setIntent(isPartial ? 'save' : 'validate')}
        showValidateButton={
          isReadOnly ||
          userRole === Role.Tablet ||
          isPartial ||
          loaderData.state !== OrderState.Validated
        }
        showPendingCertificationButton={
          !isReadOnly &&
          userRole !== Role.Tablet &&
          !isPartial &&
          loaderData.state !== OrderState.PendingCertification
        }
        pendingCertificationButtonText={t('validation:pendingCertification')}
        onBeforePendingCertification={() => setIntent('pending-certification')}
      />
      {isBasicAction && (
        <Typography size="S" colorText={'red'}>
          {error}
        </Typography>
      )}
    </>
  );
}
